// components/musicUpload.ts
// [내 음악] mp3 를 USB(COM 포트, Web Serial)로 보드에 직접 저장한다. 서버를 거치지 않는다.
//  1) {"c":"mu","a":"put","sz":바이트,"tx":"제목"} 한 줄을 보낸다.
//  2) 보드가 {"c":"mu","a":"put","ok":1,"sn":번호,"rdy":1} 로 답하면 mp3 를 MUSIC_CHUNK 바이트씩 보낸다.
//  3) 보드는 한 덩어리를 쓸 때마다 0x06(ACK) 한 바이트를 돌려준다. 그것을 받은 뒤에 다음 덩어리를 보낸다.
//  4) 다 받으면 {"c":"mu","a":"put","ok":1,"sn":번호,"dn":1} 로 알려 준다.
// 보드 펌웨어(music.ino)의 MUSIC_CHUNK, MUSIC_ACK 와 같아야 한다.

export const MUSIC_CHUNK = 1024;
const MUSIC_ACK = 0x06;
const BAUD_RATE = 115200;

export const MUSIC_PUT_ERROR_TEXT: Record<string, string> = {
  usb_only: '이 명령은 USB 로만 받을 수 있습니다.',
  bad_param: '파일 크기를 확인하지 못했습니다.',
  full: '저장할 수 있는 곡 수(6곡)가 가득 찼습니다. 곡을 하나 삭제한 뒤 다시 시도하세요.',
  no_space: '보드의 저장 공간이 부족합니다. 곡을 삭제하거나 더 작은 파일(모노·낮은 비트레이트)로 바꿔 보세요.',
  open_fail: '보드에 파일을 만들지 못했습니다.',
  timeout: '보드가 데이터를 받다가 멈췄습니다. USB 선을 확인하고 다시 시도하세요.',
  write_fail: '보드에 쓰는 중 오류가 났습니다. 저장 공간을 확인하세요.',
};

export interface MusicUploadHandlers {
  onStatus: (text: string) => void;
  onProgress: (sent: number, total: number) => void;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const isSerialSupported = (): boolean =>
  typeof navigator !== 'undefined' && !!(navigator as any).serial;

// 성공하면 보드가 정한 곡 번호(sn)를 돌려준다. 실패하면 Error 를 던진다. (사용자가 포트 선택을 취소하면 name === 'NotFoundError')
export async function uploadMusicViaUsb(file: File, title: string, h: MusicUploadHandlers): Promise<number> {
  const serial = (navigator as any).serial;
  if (!serial) throw new Error('이 브라우저는 USB(COM 포트) 전송을 지원하지 않습니다. PC 의 Chrome 또는 Edge 를 사용하세요.');

  let port: any = null;
  let reader: any = null;
  let writer: any = null;
  let alive = true;
  let ackCount = 0;
  const lines: string[] = [];
  let ackWaiters: Array<() => void> = [];

  try {
    h.onStatus('COM 포트 선택 중…');
    port = await serial.requestPort();
    await port.open({ baudRate: BAUD_RATE, bufferSize: 65536 });
    try { await port.setSignals({ dataTerminalReady: false, requestToSend: false }); } catch (e) { /* 무시 */ }

    // 수신 루프: ACK(0x06) 바이트는 세고, 나머지는 한 줄씩 모은다.
    reader = port.readable.getReader();
    const dec = new TextDecoder();
    let buf = '';
    const readLoop = (async () => {
      while (alive) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        const text: number[] = [];
        for (let i = 0; i < value.length; i++) {
          if (value[i] === MUSIC_ACK) {
            ackCount++;
            const w = ackWaiters;
            ackWaiters = [];
            w.forEach((f) => f());
          } else {
            text.push(value[i]);
          }
        }
        if (text.length) {
          buf += dec.decode(new Uint8Array(text), { stream: true });
          let i;
          while ((i = buf.indexOf('\n')) >= 0) {
            lines.push(buf.slice(0, i).trim());
            buf = buf.slice(i + 1);
          }
        }
      }
    })().catch(() => { /* 포트 닫힘 */ });

    const waitReply = async (pred: (j: any) => boolean, ms: number) => {
      const t0 = Date.now();
      while (Date.now() - t0 < ms) {
        for (const l of lines) {
          if (l[0] !== '{') continue;
          try {
            const j = JSON.parse(l);
            if (j.c === 'mu' && j.a === 'put' && pred(j)) return j;
          } catch (e) { /* 일반 로그 줄 */ }
        }
        await sleep(50);
      }
      return null;
    };

    const waitAck = (target: number, ms: number) => new Promise<boolean>((resolve) => {
      if (ackCount >= target) { resolve(true); return; }
      const timer = setTimeout(() => resolve(false), ms);
      const check = () => {
        if (ackCount >= target) { clearTimeout(timer); resolve(true); }
        else ackWaiters.push(check);
      };
      ackWaiters.push(check);
    });

    h.onStatus('보드 준비 대기 중… (약 3초)');
    await sleep(3000);

    writer = port.writable.getWriter();
    const enc = new TextEncoder();
    lines.length = 0;
    await writer.write(enc.encode(JSON.stringify({ c: 'mu', a: 'put', sz: file.size, tx: title }) + '\n'));

    h.onStatus('보드 응답 대기 중… (최대 20초)');
    const rdy = await waitReply((j) => j.ok === 0 || j.rdy === 1, 20000);
    if (!rdy) throw new Error('보드 응답 없음 (PROG 포트인지, 시리얼 모니터가 닫혀 있는지, 펌웨어가 내 음악을 지원하는지 확인)');
    if (!rdy.ok) throw new Error(MUSIC_PUT_ERROR_TEXT[rdy.err] || `보드가 저장을 거부했습니다. (${rdy.err || '알 수 없음'})`);
    const sn: number = rdy.sn;

    h.onStatus('보드로 전송 중…');
    const data = new Uint8Array(await file.arrayBuffer());
    let sent = 0;
    ackCount = 0;
    let acked = 0;
    while (sent < data.length) {
      const end = Math.min(sent + MUSIC_CHUNK, data.length);
      await writer.write(data.subarray(sent, end));
      acked++;
      if (!(await waitAck(acked, 8000))) {
        // 보드가 중간에 실패했다면 이유를 알려 준다
        const fail = await waitReply((j) => j.ok === 0, 1500);
        throw new Error(fail ? (MUSIC_PUT_ERROR_TEXT[fail.err] || `보드 오류 (${fail.err})`) : '보드가 응답하지 않습니다. 전송을 중단합니다.');
      }
      sent = end;
      h.onProgress(sent, data.length);
    }

    h.onStatus('보드가 파일을 마무리하는 중…');
    const done = await waitReply((j) => j.dn === 1 || j.ok === 0, 15000);
    if (!done) throw new Error('보드의 저장 완료 응답이 없습니다.');
    if (!done.ok) throw new Error(MUSIC_PUT_ERROR_TEXT[done.err] || `보드 오류 (${done.err})`);
    void readLoop;
    return sn;
  } finally {
    alive = false;
    try { writer?.releaseLock(); } catch (e) { /* */ }
    try { await reader?.cancel(); } catch (e) { /* */ }
    try { reader?.releaseLock(); } catch (e) { /* */ }
    try { await port?.close(); } catch (e) { /* */ }
  }
}
